import React, { useState } from 'react';
import { PageHeader } from '@/components/ui/PageHeader/PageHeader';
import { PageContainer } from '@/components/ui/PageContainer/PageContainer';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { 
  Building2, 
  Sparkles, 
  Search, 
  FileText, 
  Upload, 
  Download,
  Globe,
  MapPin,
  CheckCircle,
  AlertCircle,
  Zap,
  TrendingUp,
  Users,
  Calendar,
  Filter,
  List,
  Star,
  Plane,
  RefreshCw,
  PlusCircle,
  Edit3,
  Trash2,
  Eye
} from 'lucide-react';

type TabId = 'overview' | 'venues' | 'research-tool';

interface TabConfig {
  id: TabId;
  label: string;
  icon: React.ReactNode;
}

const tabs: TabConfig[] = [
  { 
    id: 'overview', 
    label: 'Getting Started', 
    icon: <Sparkles className="w-4 h-4" /> 
  },
  { 
    id: 'venues', 
    label: 'Venues & AI Research', 
    icon: <Building2 className="w-4 h-4" /> 
  },
  { 
    id: 'research-tool', 
    label: 'Research & Proposals', 
    icon: <FileText className="w-4 h-4" /> 
  },
];

export function Documentation() {
  const [activeTab, setActiveTab] = useState<TabId>('overview');

  return (
    <PageContainer>
      <PageHeader
        title="Documentation"
        description="Learn how to use Moniker's venue management and research tools"
      />

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-2 overflow-x-auto" aria-label="Documentation sections">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                flex items-center gap-2 px-4 py-3 border-b-2 font-medium text-sm whitespace-nowrap transition-colors
                ${activeTab === tab.id 
                  ? 'border-primary-600 text-primary-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
              aria-current={activeTab === tab.id ? 'page' : undefined}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="space-y-6">
        {activeTab === 'overview' && <OverviewTab />}
        {activeTab === 'venues' && <VenuesTab />}
        {activeTab === 'research-tool' && <ResearchToolTab />}
      </div>
    </PageContainer>
  );
}

function OverviewTab() {
  return (
    <div className="space-y-6">
      {/* Introduction */}
      <Card>
        <CardHeader>
          <CardTitle>Welcome to Moniker</CardTitle>
          <CardDescription>
            Your complete solution for venue management and event planning
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-gray-700 mb-4">
            Moniker provides two powerful systems to streamline your venue research and event planning workflow:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex gap-3 p-4 bg-primary-50 rounded-lg border border-primary-100">
              <div className="flex-shrink-0 w-10 h-10 bg-primary-600 rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Venues Database</h3>
                <p className="text-sm text-gray-600">
                  Build and maintain your organization's comprehensive venue library with AI-powered discovery
                </p>
              </div>
            </div>
            <div className="flex gap-3 p-4 bg-success-50 rounded-lg border border-success-100">
              <div className="flex-shrink-0 w-10 h-10 bg-success-600 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Research & Proposals</h3>
                <p className="text-sm text-gray-600">
                  Find perfect venues for specific events using AI-powered search and manage your shortlists
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Start Guide */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Start Guide</CardTitle>
          <CardDescription>Get up and running in minutes</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center font-semibold">
                1
              </div>
              <div>
                <h4 className="font-medium text-gray-900 mb-1">Build Your Venue Database</h4>
                <p className="text-sm text-gray-600 mb-2">
                  Start by adding venues to your database through manual entry, CSV import, or AI research.
                </p>
                <div className="flex gap-2">
                  <Badge variant="secondary">Manual Entry</Badge>
                  <Badge variant="secondary">CSV Import</Badge>
                  <Badge variant="secondary">AI Discovery</Badge>
                </div>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center font-semibold">
                2
              </div>
              <div>
                <h4 className="font-medium text-gray-900 mb-1">Enrich Venue Data</h4>
                <p className="text-sm text-gray-600 mb-2">
                  Use AI to automatically gather amenities, TripAdvisor reviews, and location data for your venues.
                </p>
                <div className="flex gap-2">
                  <Badge variant="secondary">Amenities</Badge>
                  <Badge variant="secondary">Reviews</Badge>
                  <Badge variant="secondary">Airports</Badge>
                </div>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center font-semibold">
                3
              </div>
              <div>
                <h4 className="font-medium text-gray-900 mb-1">Create Event Research Projects</h4>
                <p className="text-sm text-gray-600 mb-2">
                  Use the Research & Proposal tool to find the perfect venues for your client events.
                </p>
                <div className="flex gap-2">
                  <Badge variant="secondary">AI Search</Badge>
                  <Badge variant="secondary">Shortlists</Badge>
                  <Badge variant="secondary">RFPs</Badge>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Key Features at a Glance */}
      <Card>
        <CardHeader>
          <CardTitle>Key Features</CardTitle>
          <CardDescription>Everything you need to manage venues and events</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <FeatureItem
              icon={<Sparkles className="w-5 h-5" />}
              title="AI-Powered Discovery"
              description="Find hotels automatically using web search"
            />
            <FeatureItem
              icon={<Search className="w-5 h-5" />}
              title="Smart Search"
              description="Natural language venue queries"
            />
            <FeatureItem
              icon={<Filter className="w-5 h-5" />}
              title="Advanced Filtering"
              description="15+ filters for precise results"
            />
            <FeatureItem
              icon={<Upload className="w-5 h-5" />}
              title="CSV Import/Export"
              description="Bulk data management made easy"
            />
            <FeatureItem
              icon={<TrendingUp className="w-5 h-5" />}
              title="TripAdvisor Integration"
              description="Ratings and reviews included"
            />
            <FeatureItem
              icon={<Plane className="w-5 h-5" />}
              title="Airport Proximity"
              description="Automatic distance calculations"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function VenuesTab() {
  return (
    <div className="space-y-6">
      {/* Overview */}
      <Card>
        <CardHeader>
          <CardTitle as="h2">Venues Database & AI Research</CardTitle>
          <CardDescription>
            Build and maintain your organization's comprehensive venue library
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-gray-700 mb-4">
            The Venues system allows you to create, manage, and enrich a searchable database of hotels and event spaces. 
            Add venues manually, import via CSV, or use AI to discover new properties automatically.
          </p>
          <div className="bg-info-50 border border-info-200 rounded-lg p-4">
            <div className="flex gap-3">
              <AlertCircle className="w-5 h-5 text-info-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-info-900 font-medium mb-1">Access Required</p>
                <p className="text-sm text-info-800">
                  Navigate to <strong>Organization → Venues</strong> to access the venues database
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Managing Venues */}
      <Card>
        <CardHeader>
          <CardTitle as="h3">Managing Your Venue Database</CardTitle>
          <CardDescription>Add, edit, and organize venue records</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Add Venues */}
            <div>
              <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                <PlusCircle className="w-4 h-4 text-primary-600" />
                Adding Venues
              </h4>
              <div className="space-y-3">
                <MethodCard
                  title="Manual Entry"
                  description="Click 'Add Venue' to create a single venue record with all details"
                  icon={<Edit3 className="w-4 h-4" />}
                />
                <MethodCard
                  title="CSV Import"
                  description="Upload a CSV file to add multiple venues at once. Download the template to see the required format."
                  icon={<Upload className="w-4 h-4" />}
                />
                <MethodCard
                  title="AI Research"
                  description="Let AI discover hotels in any city or country automatically. Great for building your database quickly."
                  icon={<Sparkles className="w-4 h-4" />}
                  highlighted
                />
              </div>
            </div>

            {/* Edit & Delete */}
            <div>
              <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                <Edit3 className="w-4 h-4 text-primary-600" />
                Editing & Deleting
              </h4>
              <p className="text-sm text-gray-600 mb-3">
                Use the actions column to edit or delete individual venues. You can also:
              </p>
              <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside ml-4">
                <li>Select multiple venues for bulk operations</li>
                <li>Delete multiple venues at once</li>
                <li>Refresh airports for selected venues</li>
                <li>Trigger AI enrichment in bulk</li>
              </ul>
            </div>

            {/* Export */}
            <div>
              <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                <Download className="w-4 h-4 text-primary-600" />
                Exporting Data
              </h4>
              <p className="text-sm text-gray-600 mb-2">
                Export your venues to CSV format with three options:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-sm font-medium text-gray-900 mb-1">This Page</p>
                  <p className="text-xs text-gray-600">Export currently visible venues</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-sm font-medium text-gray-900 mb-1">All Filtered</p>
                  <p className="text-xs text-gray-600">Export all venues matching your filters</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-sm font-medium text-gray-900 mb-1">All Venues</p>
                  <p className="text-xs text-gray-600">Export your entire database</p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* AI Research */}
      <Card>
        <CardHeader>
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-primary-600" />
            </div>
            <div className="flex-1">
              <CardTitle as="h3">AI Hotel Research</CardTitle>
              <CardDescription>
                Discover hotels automatically using AI-powered web search
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <div>
              <p className="text-gray-700 mb-4">
                Navigate to <strong>Venues → AI Research</strong> to launch intelligent web searches that discover hotels 
                matching your criteria. The AI agent searches multiple sources and creates venue records automatically.
              </p>
            </div>

            {/* How It Works */}
            <div>
              <h4 className="font-medium text-gray-900 mb-3">How It Works</h4>
              <div className="space-y-3">
                <StepCard
                  number={1}
                  title="Select Locations"
                  description="Add up to 10 cities or countries. The AI will search each location for suitable hotels."
                  icon={<MapPin className="w-4 h-4" />}
                />
                <StepCard
                  number={2}
                  title="Set Criteria"
                  description="Define minimum rooms, capacity, and star rating. Add custom instructions if needed."
                  icon={<Filter className="w-4 h-4" />}
                />
                <StepCard
                  number={3}
                  title="Choose Search Depth"
                  description="Select 1-5 sequential searches per location. More searches = more venues discovered (~10 per search)."
                  icon={<Search className="w-4 h-4" />}
                />
                <StepCard
                  number={4}
                  title="AI Searches & Enriches"
                  description="The AI searches booking sites, hotel websites, and TripAdvisor to gather comprehensive data."
                  icon={<Zap className="w-4 h-4" />}
                />
                <StepCard
                  number={5}
                  title="Review Results"
                  description="New venues are added to your database automatically with full details and enrichment data."
                  icon={<CheckCircle className="w-4 h-4" />}
                />
              </div>
            </div>

            {/* Research Features */}
            <div>
              <h4 className="font-medium text-gray-900 mb-3">Research Features</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <FeatureBox
                  icon={<Globe className="w-4 h-4" />}
                  title="Country-Level Search"
                  description="Search entire countries like 'Spain' or 'Croatia' for nationwide coverage"
                />
                <FeatureBox
                  icon={<MapPin className="w-4 h-4" />}
                  title="Smart Deduplication"
                  description="AI avoids creating duplicates of venues already in your database"
                />
                <FeatureBox
                  icon={<RefreshCw className="w-4 h-4" />}
                  title="Sequential Searches"
                  description="Run multiple searches per location to discover more unique properties"
                />
                <FeatureBox
                  icon={<Star className="w-4 h-4" />}
                  title="TripAdvisor Data"
                  description="Includes ratings, review counts, and recent review excerpts"
                />
                <FeatureBox
                  icon={<Plane className="w-4 h-4" />}
                  title="Auto Geocoding"
                  description="Automatically maps addresses and assigns nearest airports"
                />
                <FeatureBox
                  icon={<FileText className="w-4 h-4" />}
                  title="Custom Instructions"
                  description="Guide the AI with specific requirements or preferences"
                />
              </div>
            </div>

            {/* Tips */}
            <div className="bg-success-50 border border-success-200 rounded-lg p-4">
              <h4 className="font-medium text-success-900 mb-2 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Best Practices
              </h4>
              <ul className="text-sm text-success-800 space-y-1">
                <li>• Start with 1-2 searches per location, then run more if needed</li>
                <li>• Use custom instructions for specific requirements like "boutique hotels" or "spa facilities"</li>
                <li>• Research history shows all past jobs with results and costs</li>
                <li>• Each search typically takes 30-60 seconds to complete</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filtering & Search */}
      <Card>
        <CardHeader>
          <CardTitle as="h3">Finding Venues in Your Database</CardTitle>
          <CardDescription>Use powerful filters to find exactly what you need</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-gray-700">
              The venues table includes 15+ filters to help you find specific properties:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <FilterItem label="Location" items={['City', 'Country', 'State']} />
              <FilterItem label="Capacity" items={['Min Rooms', 'Min Capacity', 'Meeting Spaces']} />
              <FilterItem label="Quality" items={['Star Rating', 'TripAdvisor Rating']} />
              <FilterItem label="Location" items={['Max Airport Distance']} />
              <FilterItem label="Amenities" items={['Spa', 'Beach', 'Golf', 'All-Inclusive']} />
              <FilterItem label="Status" items={['Research Status', 'Data Freshness']} />
            </div>
            <div className="bg-info-50 border border-info-200 rounded-lg p-3">
              <p className="text-sm text-info-900">
                <strong>Tip:</strong> Click the "Filters" button to expand the advanced filter panel with all options
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data Enrichment */}
      <Card>
        <CardHeader>
          <CardTitle as="h3">AI Enrichment</CardTitle>
          <CardDescription>Automatically enhance venue data with AI</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-gray-700 mb-4">
              Trigger AI enrichment on any venue to automatically gather missing information from the web:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-2">What Gets Enriched</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-success-600 flex-shrink-0 mt-0.5" />
                    <span>Amenities (restaurants, bars, pools, spa)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-success-600 flex-shrink-0 mt-0.5" />
                    <span>TripAdvisor ratings and reviews</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-success-600 flex-shrink-0 mt-0.5" />
                    <span>Property type and features</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-success-600 flex-shrink-0 mt-0.5" />
                    <span>Beach access and golf facilities</span>
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-2">Enrichment Options</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li className="flex items-start gap-2">
                    <Zap className="w-4 h-4 text-primary-600 flex-shrink-0 mt-0.5" />
                    <span>Single venue enrichment (click AI icon)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Zap className="w-4 h-4 text-primary-600 flex-shrink-0 mt-0.5" />
                    <span>Bulk enrichment (select multiple venues)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Zap className="w-4 h-4 text-primary-600 flex-shrink-0 mt-0.5" />
                    <span>Status tracking (pending → enriched)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Zap className="w-4 h-4 text-primary-600 flex-shrink-0 mt-0.5" />
                    <span>Reset option for stuck enrichments</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function ResearchToolTab() {
  return (
    <div className="space-y-6">
      {/* Overview */}
      <Card>
        <CardHeader>
          <CardTitle as="h2">Research & Proposal Generation Tool</CardTitle>
          <CardDescription>
            Find the perfect venues for your client events using AI-powered search
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-gray-700 mb-4">
            The Research & Proposal tool is designed for event planners working on specific client projects. 
            It searches your existing venue database using natural language queries and helps you build shortlists 
            for each destination you're considering.
          </p>
          <div className="bg-info-50 border border-info-200 rounded-lg p-4">
            <div className="flex gap-3">
              <AlertCircle className="w-5 h-5 text-info-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-info-900 font-medium mb-1">How to Access</p>
                <p className="text-sm text-info-800">
                  Navigate to <strong>Tools → Research & Proposal Generation</strong> or create/select an event from your clients page
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Getting Started */}
      <Card>
        <CardHeader>
          <CardTitle as="h3">Getting Started</CardTitle>
          <CardDescription>Set up your research project in minutes</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <StepCard
              number={1}
              title="Select an Event"
              description="Choose an existing event or create a new one. The tool uses event details (attendee count, requirements) to provide better venue recommendations."
              icon={<Calendar className="w-4 h-4" />}
            />
            <StepCard
              number={2}
              title="Add Destinations"
              description="Add up to 10 destinations where you're looking for venues. These can be cities, countries, or regions."
              icon={<MapPin className="w-4 h-4" />}
            />
            <StepCard
              number={3}
              title="Search for Venues"
              description="Use natural language to search across all destinations at once. The AI understands your requirements and ranks results by relevance."
              icon={<Search className="w-4 h-4" />}
            />
            <StepCard
              number={4}
              title="Build Shortlists"
              description="Add promising venues to your shortlist for each destination. Add notes and compare options side by side."
              icon={<List className="w-4 h-4" />}
            />
          </div>
        </CardContent>
      </Card>

      {/* Context Sidebar */}
      <Card>
        <CardHeader>
          <CardTitle as="h3">Event Context</CardTitle>
          <CardDescription>The sidebar keeps important information at your fingertips</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-gray-700">
              The context sidebar displays throughout your research session:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <ContextItem
                icon={<Users className="w-4 h-4" />}
                title="Client Info"
                description="Company name and contact details"
              />
              <ContextItem
                icon={<Calendar className="w-4 h-4" />}
                title="Event Details"
                description="Event name, dates, and attendee count"
              />
              <ContextItem
                icon={<FileText className="w-4 h-4" />}
                title="Requirements"
                description="Event notes and specific needs"
              />
            </div>
            <div className="bg-success-50 border border-success-200 rounded-lg p-3">
              <p className="text-sm text-success-900">
                <strong>Tip:</strong> You can edit event requirements directly from the sidebar to refine your searches
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* AI Search */}
      <Card>
        <CardHeader>
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-primary-600" />
            </div>
            <div className="flex-1">
              <CardTitle as="h3">AI-Powered Venue Search</CardTitle>
              <CardDescription>
                Use natural language to find the perfect venues
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <div>
              <h4 className="font-medium text-gray-900 mb-3">How AI Search Works</h4>
              <p className="text-gray-700 mb-4">
                The system uses a 3-stage AI pipeline to understand your needs and find the best matches:
              </p>
              <div className="space-y-3">
                <PipelineStage
                  stage="Stage 1"
                  title="Extract Criteria"
                  description="AI analyzes your natural language query and extracts structured filters (rooms, capacity, location, etc.)"
                  color="blue"
                />
                <PipelineStage
                  stage="Stage 2"
                  title="Query Database"
                  description="The system searches your venue database using the extracted criteria across all destinations"
                  color="purple"
                />
                <PipelineStage
                  stage="Stage 3"
                  title="AI Ranking"
                  description="Results are ranked by relevance with match scores and reasoning for each venue"
                  color="green"
                />
              </div>
            </div>

            <div>
              <h4 className="font-medium text-gray-900 mb-3">Example Search Queries</h4>
              <div className="space-y-2">
                <SearchExample query="Luxury 5-star hotels with spa facilities" />
                <SearchExample query="Beachfront resorts with at least 200 rooms" />
                <SearchExample query="Hotels near the airport with large conference capacity" />
                <SearchExample query="All-inclusive properties suitable for corporate retreats" />
              </div>
            </div>

            <div>
              <h4 className="font-medium text-gray-900 mb-3">Manual Filters</h4>
              <p className="text-sm text-gray-600 mb-3">
                Prefer exact criteria? Click "Refine Filters" to bypass natural language and set explicit filters:
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Badge variant="secondary">Min Rooms</Badge>
                <Badge variant="secondary">Min Capacity</Badge>
                <Badge variant="secondary">Star Rating</Badge>
                <Badge variant="secondary">Airport Distance</Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results & Ranking */}
      <Card>
        <CardHeader>
          <CardTitle as="h3">Understanding Search Results</CardTitle>
          <CardDescription>AI provides match scores and reasoning for each venue</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-gray-700">
              When using natural language search, each venue receives AI-generated metadata:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-900">Match Score</span>
                  <Badge variant="success">95</Badge>
                </div>
                <p className="text-xs text-gray-600">
                  0-100 rating showing how well the venue matches your requirements
                </p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="mb-2">
                  <span className="text-sm font-medium text-gray-900">Reasoning</span>
                </div>
                <p className="text-xs text-gray-600">
                  1-2 sentence explanation of why this venue is a good (or poor) match
                </p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="mb-2">
                  <span className="text-sm font-medium text-gray-900">Recommendation</span>
                </div>
                <div className="flex flex-wrap gap-1">
                  <Badge variant="success" className="text-xs">Highly Recommended</Badge>
                  <Badge variant="default" className="text-xs">Recommended</Badge>
                </div>
              </div>
            </div>
            <div className="bg-info-50 border border-info-200 rounded-lg p-3">
              <p className="text-sm text-info-900">
                <strong>Note:</strong> Manual filter searches skip AI ranking and show unranked results
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Shortlist Management */}
      <Card>
        <CardHeader>
          <CardTitle as="h3">Managing Shortlists</CardTitle>
          <CardDescription>Organize and compare your venue options</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-gray-700 mb-4">
              Each destination has its own shortlist where you can save promising venues:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <ShortlistFeature
                icon={<PlusCircle className="w-4 h-4" />}
                title="Add to Shortlist"
                description="Click the heart icon or 'Add to Shortlist' button on any venue"
              />
              <ShortlistFeature
                icon={<FileText className="w-4 h-4" />}
                title="Add Notes"
                description="Keep track of thoughts, pricing, or specific details for each venue"
              />
              <ShortlistFeature
                icon={<Eye className="w-4 h-4" />}
                title="View Details"
                description="Click any venue to see complete information including amenities and reviews"
              />
              <ShortlistFeature
                icon={<Trash2 className="w-4 h-4" />}
                title="Remove Items"
                description="Remove individual venues or clear the entire shortlist at once"
              />
            </div>
            <div className="bg-success-50 border border-success-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-success-900 mb-2">Future Features</h4>
              <p className="text-sm text-success-800">
                Upcoming steps will let you send RFPs to shortlisted venues, compare proposals, 
                and generate client-ready presentations directly from your shortlist.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Workflow Steps */}
      <Card>
        <CardHeader>
          <CardTitle as="h3">Complete Workflow</CardTitle>
          <CardDescription>The tool supports a 4-step process (Step 1 currently active)</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <WorkflowStep
              number={1}
              title="Research"
              description="Search venues and build shortlists for each destination"
              status="active"
            />
            <WorkflowStep
              number={2}
              title="RFPs"
              description="Send requests for proposals to shortlisted venues"
              status="upcoming"
            />
            <WorkflowStep
              number={3}
              title="Selection"
              description="Compare responses and select final venues"
              status="upcoming"
            />
            <WorkflowStep
              number={4}
              title="Proposal"
              description="Generate client-ready proposals"
              status="upcoming"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Helper Components

function FeatureItem({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) {
  return (
    <div className="flex gap-3">
      <div className="flex-shrink-0 w-8 h-8 bg-primary-100 text-primary-600 rounded-lg flex items-center justify-center">
        {icon}
      </div>
      <div>
        <h4 className="text-sm font-medium text-gray-900">{title}</h4>
        <p className="text-xs text-gray-600">{description}</p>
      </div>
    </div>
  );
}

function MethodCard({ title, description, icon, highlighted }: { title: string; description: string; icon: React.ReactNode; highlighted?: boolean }) {
  return (
    <div className={`p-3 rounded-lg border ${highlighted ? 'bg-primary-50 border-primary-200' : 'bg-gray-50 border-gray-200'}`}>
      <div className="flex items-start gap-3">
        <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${highlighted ? 'bg-primary-600 text-white' : 'bg-gray-300 text-gray-700'}`}>
          {icon}
        </div>
        <div className="flex-1">
          <h5 className="text-sm font-medium text-gray-900 mb-1">{title}</h5>
          <p className="text-xs text-gray-600">{description}</p>
        </div>
      </div>
    </div>
  );
}

function StepCard({ number, title, description, icon }: { number: number; title: string; description: string; icon: React.ReactNode }) {
  return (
    <div className="flex gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
      <div className="flex-shrink-0 w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center font-semibold text-sm">
        {number}
      </div>
      <div className="flex-1">
        <h5 className="text-sm font-medium text-gray-900 mb-1 flex items-center gap-2">
          {icon}
          {title}
        </h5>
        <p className="text-xs text-gray-600">{description}</p>
      </div>
    </div>
  );
}

function FeatureBox({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) {
  return (
    <div className="p-3 bg-white rounded-lg border border-gray-200">
      <div className="flex items-start gap-2 mb-1">
        <div className="flex-shrink-0 w-6 h-6 bg-primary-100 text-primary-600 rounded flex items-center justify-center">
          {icon}
        </div>
        <h5 className="text-sm font-medium text-gray-900">{title}</h5>
      </div>
      <p className="text-xs text-gray-600 ml-8">{description}</p>
    </div>
  );
}

function FilterItem({ label, items }: { label: string; items: string[] }) {
  return (
    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
      <p className="text-xs font-medium text-gray-700 mb-2">{label}</p>
      <div className="flex flex-wrap gap-1">
        {items.map((item, idx) => (
          <Badge key={idx} variant="secondary" className="text-xs">{item}</Badge>
        ))}
      </div>
    </div>
  );
}

function ContextItem({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) {
  return (
    <div className="text-center">
      <div className="w-12 h-12 bg-primary-100 text-primary-600 rounded-lg flex items-center justify-center mx-auto mb-2">
        {icon}
      </div>
      <h5 className="text-sm font-medium text-gray-900 mb-1">{title}</h5>
      <p className="text-xs text-gray-600">{description}</p>
    </div>
  );
}

function PipelineStage({ stage, title, description, color }: { stage: string; title: string; description: string; color: string }) {
  const colorClasses = {
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    purple: 'bg-purple-50 border-purple-200 text-purple-700',
    green: 'bg-green-50 border-green-200 text-green-700',
  };

  return (
    <div className={`p-3 rounded-lg border ${colorClasses[color as keyof typeof colorClasses]}`}>
      <div className="flex items-start gap-3">
        <Badge variant="secondary" className="flex-shrink-0">{stage}</Badge>
        <div className="flex-1">
          <h5 className="text-sm font-medium text-gray-900 mb-1">{title}</h5>
          <p className="text-xs text-gray-700">{description}</p>
        </div>
      </div>
    </div>
  );
}

function SearchExample({ query }: { query: string }) {
  return (
    <div className="flex items-start gap-2 p-2 bg-gray-50 rounded border border-gray-200">
      <Search className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
      <p className="text-sm text-gray-700 italic">"{query}"</p>
    </div>
  );
}

function ShortlistFeature({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) {
  return (
    <div className="flex gap-3">
      <div className="flex-shrink-0 w-8 h-8 bg-success-100 text-success-600 rounded-lg flex items-center justify-center">
        {icon}
      </div>
      <div>
        <h5 className="text-sm font-medium text-gray-900 mb-1">{title}</h5>
        <p className="text-xs text-gray-600">{description}</p>
      </div>
    </div>
  );
}

function WorkflowStep({ number, title, description, status }: { number: number; title: string; description: string; status: 'active' | 'upcoming' }) {
  return (
    <div className={`p-4 rounded-lg border ${status === 'active' ? 'bg-primary-50 border-primary-200' : 'bg-gray-50 border-gray-200'}`}>
      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg mb-3 ${status === 'active' ? 'bg-primary-600 text-white' : 'bg-gray-300 text-gray-600'}`}>
        {number}
      </div>
      <h5 className="text-sm font-medium text-gray-900 mb-1">{title}</h5>
      <p className="text-xs text-gray-600 mb-2">{description}</p>
      {status === 'upcoming' && (
        <Badge variant="default" className="text-xs">Coming Soon</Badge>
      )}
    </div>
  );
}

